import React from 'react';
import { murmurhash } from '../../../lib/js/hash';
import {
  ColumnIdentifier,
  Table,
  TableBody,
  TableCell,
  TableCellDescription,
  TableCellPageLink,
  TableHead,
  TableModifier,
  TableRow,
} from '../../../components/organisms/Table';
import { Pagination, PaginationItemWithPageLink } from '../../../_lib/components/Pagination';
import ResultListSettings from '../../../components/molecules/ResultListSettings/ResultListSettings';
import { DateFormat } from '../../../_lib/components/DateFormat';
import { ContentAttachmentList } from '../../../components/molecules/ContentAttachmentList/ContentAttachmentList';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { Facets } from '../../../types/Facets';
import { FACETS } from '../../../components/molecules/ResultListSettings/ResultListSettings.enums';
import { DisplayOptions, getDisplayedTextByConfig } from '../PageListDetails.utils';

export type PageListTable = {
  displayOptions: DisplayOptions;
  childPages?: any[]; // <--- "categories?: ICategory[];" is part of
  sort: any;
  facets?: Facets;
  pageItems: PaginationItemWithPageLink[];
  resetAllFiltersUrl?: {
    url: string;
  };
};

export function PageListTable(props: PageListTable) {
  const { t } = useSonTranslation('PageListDetails');

  const titleLabel = t('pageListTable.label.title');
  const shortDescriptionLabel = t('pageListTable.label.shortDescriptionLabel');
  const dateLabel = t('pageListTable.label.dateLabel');
  const attachmentsLabel = t('pageListTable.label.attachmentsLabel');

  const showColumn = (columnIdentifier: string) => {
    return !props.displayOptions.hideColumns.includes(columnIdentifier);
  };

  const extractRows = () => {
    return props.childPages?.map(resultItem => (
      <TableRow key={murmurhash(resultItem)}>
        <TableCellPageLink
          label={titleLabel}
          pageLink={resultItem.pageLink}
          pagePackState={resultItem.packState}
          archiveDate={resultItem.archiveDate}
          integrated={resultItem.integrated}
          categories={resultItem.categories}
        />
        <TableCellDescription
          label={shortDescriptionLabel}
          description={getDisplayedTextByConfig(resultItem, props.displayOptions.displayedInformationType)}
          externalLink={resultItem.externalLink}
        />
        <TableCell dataHead={dateLabel}>
          <DateFormat date={resultItem.contentDate} />
        </TableCell>
        {showColumn(ColumnIdentifier.attachment) && (
          <TableCell dataHead={attachmentsLabel}>
            <ContentAttachmentList attachments={resultItem?.attachments || []} />
          </TableCell>
        )}
      </TableRow>
    ));
  };

  return (
    <div className="page-list-table">
      <ResultListSettings
        key={murmurhash(props)}
        sort={props.sort}
        facets={props.facets}
        wantedFacets={[FACETS.FQ_ARCHIVEDATE, FACETS.FQ_PAGE_CATEGORY_TITLES]}
        resetAllFiltersUrl={props.resetAllFiltersUrl}
      />
      {props.childPages?.length !== 0
        ? (
            <>
              <Table caption={t('pageListTable.caption')} captionHidden>
                <TableHead>
                  <TableRow>
                    <TableCell modifier={TableModifier.MWIDTH}>{titleLabel}</TableCell>
                    <TableCell>{shortDescriptionLabel}</TableCell>
                    <TableCell modifier={TableModifier.XXSWIDTH}>{dateLabel}</TableCell>
                    {showColumn(ColumnIdentifier.attachment) && (
                      <TableCell modifier={TableModifier.SWIDTH}>{attachmentsLabel}</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>{extractRows()}</TableBody>
              </Table>
              <Pagination pageItems={props.pageItems} />
            </>
          )
        : null}
    </div>
  );
}
